// In CSTickets.jsx
import React, { useState, useEffect, useRef } from "react"
import TicketBrowser from "./TicketBrowser"
import TicketDetails from "./TicketDetails"
import { navigate } from "vike/client/router"

export default function CSTickets() {
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const lastPathRef = useRef(window.location.pathname)
  const isNavigatingRef = useRef(false) // Add this reference to track intentional navigation

  // Function to parse ticket ID from URL
  const getTicketIdFromUrl = (pathname) => {
    const match = pathname.match(/\/admin\/customer-support\/tickets\/([^\/]+)/)
    return match ? match[1] : null
  }

  // Initialize from URL on mount
  useEffect(() => {
    const ticketId = getTicketIdFromUrl(window.location.pathname)
    setSelectedTicketId(ticketId)
    setIsInitialized(true)
  }, [])

  // Monitor URL changes (including side panel clicks)
  useEffect(() => {
    const checkUrlChanges = () => {
      const currentPath = window.location.pathname

      // Skip check if we're in the middle of an intentional navigation
      if (isNavigatingRef.current) {
        return
      }

      // Only update if the path has actually changed
      if (currentPath !== lastPathRef.current) {
        lastPathRef.current = currentPath
        const ticketId = getTicketIdFromUrl(currentPath)
        setSelectedTicketId(ticketId)
      }
    }

    // Check on popstate (back/forward buttons)
    const handlePopState = () => {
      checkUrlChanges()
    }

    // Use a larger interval to reduce processing load
    const intervalId = setInterval(checkUrlChanges, 300)

    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
      clearInterval(intervalId)
    }
  }, [])

  // Handle ticket selection
  const handleSelectTicket = (ticketId) => {
    isNavigatingRef.current = true // Flag that we're doing intentional navigation
    setSelectedTicketId(ticketId)
    window.history.pushState(
      null,
      "",
      `/admin/customer-support/tickets/${ticketId}`
    )
    lastPathRef.current = window.location.pathname // Update last path immediately
    setTimeout(() => {
      isNavigatingRef.current = false
    }, 500) // Reset flag after navigation completes
  }

  // Handle closing ticket details
  const handleCloseTicket = () => {
    setSelectedTicketId(null)
    navigate("/admin/customer-support/tickets")
  }

  // Don't render until we've checked the URL
  if (!isInitialized) {
    return null
  }

  if (selectedTicketId) {
    return (
      <TicketDetails ticketId={selectedTicketId} onClose={handleCloseTicket} />
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      <TicketBrowser onSelectTicket={handleSelectTicket} />
    </div>
  )
}
